import React, { useState, useEffect } from 'react';
import { gql, useLazyQuery, useApolloClient } from '@apollo/client';
import { Container, Grid, Box, TextField, Button, MenuItem, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable from 'react-data-table-component';
import CustomLoader from '../components/Loader/CustomLoader';
import { customStyles } from '../utils/tableStyleForSore';
import useGlobalStyles from '../utils/globalStyles';
import { GetOrderedItemsByTimeSlot, GetLocalStoreCustomerDeliverySlot } from '../apollo';

const GET_LOCAL_STORE_CUSTOMER_DELIVERY_SLOT = gql`
  ${GetLocalStoreCustomerDeliverySlot}
`;

const GET_ORDERED_ITEMS_BY_TIME_SLOT = gql`
  ${GetOrderedItemsByTimeSlot}
`;

const statuses = {
  '-1': 'All',
  1: 'Confirmed',
  2: 'Packing',
  3: 'Order billing',
  4: 'Ready for delivery',
  5: 'Rider Assigned',
  6: 'Out for delivery',
  7: 'Delivered',
  8: 'Cancelled',
};

const ItemsbyTimeSlot = () => {
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [orderStatus, setOrderStatus] = useState(-1);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [storeName, setStoreName] = useState('');
  const globalClasses = useGlobalStyles();
  const client = useApolloClient();
// Define the getTimeSlot function
 const getTimeSlot = (timeSlotId) => {
  const timeSlot = timeSlots.find((slot) => slot._id === timeSlotId);
  return timeSlot ? `${timeSlot.start} - ${timeSlot.end}` : 'N/A';
};
  const [pagination, setPagination] = useState({ skip: 0, limit: 10 });

  useEffect(() => {
    client
      .query({ query: GET_LOCAL_STORE_CUSTOMER_DELIVERY_SLOT })
      .then((response) => {
        const fetchedSlots =
          (response &&
            response.data &&
            response.data.getLocalStoreCustomerDeliverySlot &&
            response.data.getLocalStoreCustomerDeliverySlot.timeSlot) ||
          [];
        setTimeSlots(fetchedSlots);
      })
      .catch((error) => console.error('Error fetching time slots:', error));
  }, [client]);

  const [fetchOrderedItems, { loading: dataLoading }] = useLazyQuery(
    GET_ORDERED_ITEMS_BY_TIME_SLOT,
    {
      onCompleted: (data) => {
        setTableData(
          (data &&
            data.getOrderedItemsByTimeSlot &&
            data.getOrderedItemsByTimeSlot.timeSlotItems) ||
          []
        );
        setTotalCount(
          (data &&
            data.getOrderedItemsByTimeSlot &&
            data.getOrderedItemsByTimeSlot.totalCount) ||
          0
        );
        setStoreName(
          (data &&
            data.getOrderedItemsByTimeSlot &&
            data.getOrderedItemsByTimeSlot.storeName) ||
          ''
        );
      },
      onError: (error) => console.error('Error fetching ordered items:', error),
    }
  );

  const handleFetchItems = () => {
    if (!selectedTimeSlot) {
      alert('Please select a time slot.');
      return;
    }

    fetchOrderedItems({
      variables: {
        timeSlotId: selectedTimeSlot,
        orderDate,
        orderStatus: parseInt(orderStatus, 10),
        skip: pagination.skip,
        limit: pagination.limit,
      },
    });
  };

  const fetchAllItemsForDownload = async () => {
    try {
      const { data } = await client.query({
        query: GET_ORDERED_ITEMS_BY_TIME_SLOT,
        variables: {
          timeSlotId: selectedTimeSlot,
          orderDate,
          orderStatus: parseInt(orderStatus, 10),
          skip: 0,
          limit: totalCount,
        },
        fetchPolicy: 'network-only',
      });

      return (
        (data &&
          data.getOrderedItemsByTimeSlot &&
          data.getOrderedItemsByTimeSlot.timeSlotItems) ||
        []
      );
    } catch (error) {
      console.error('Error fetching all items for download:', error);
      return [];
    }
  };

  const downloadCSV = async () => {
    const allItems = await fetchAllItemsForDownload();

    if (!allItems.length) {
      alert('No data available to download.');
      return;
    }
 // Create the first part of the CSV with the additional details
  const detailsRows = [
    `Store Name: ${storeName || 'N/A'}`,
    `Time Slot: ${getTimeSlot(selectedTimeSlot) || 'N/A'}`,
    `Date: ${orderDate || 'N/A'}`,
    `Order Status: ${statuses[orderStatus] || 'N/A'}`,
    `Total Items: ${totalCount || 'N/A'}`,
    '',
  ];

    const headers = ['Sl No.', 'Product ID', 'Product Name', 'Total Quantity', 'Total Orders', 'Category'];
    const csvRows = [...detailsRows,headers.join(',')];

    allItems.forEach((row, index) => {
      const rowData = [
        index + 1,
        row.productId,
        row.productName,
        row.totalQty,
        row.totalOrders,
        row.category,
      ];
      csvRows.push(rowData.join(','));
    });

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'ItemsbyTimeSlot.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { name: 'Sl No.', cell: (row, index) => <>{index + 1}</> },
    { name: 'Product ID', selector: 'productId', sortable: true },
    { name: 'Product Name', selector: 'productName', sortable: true },
    { name: 'Total Quantity', selector: 'totalQty', sortable: true },
    { name: 'Total Orders', selector: 'totalOrders', sortable: true },
    { name: 'Category', selector: 'category', sortable: true },
  ];

  return (
    <Container className={globalClasses.flex}>
      <Box sx={{ mb: 3, mt: 3 }}>
        <Grid container spacing={2} alignItems="center">
        <Grid item xs={3}>
            <TextField
              select
              label="Time Slot"
              value={selectedTimeSlot}
              onChange={(e) => setSelectedTimeSlot(e.target.value)}
              fullWidth
              sx={{ input: { color: 'black' }, '.MuiSelect-select': { color: 'black' } }}
            >
              {timeSlots.length > 0 ? (
                timeSlots.map((slot) => (
                  <MenuItem key={slot._id} value={slot._id} sx={{ color: 'black' }}>
                    {slot.start} - {slot.end}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled sx={{ color: 'black' }}>No Time Slots Available</MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="Select Date"
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ input: { color: 'black' } }}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              select
              label="Order Status"
              value={orderStatus}
              onChange={(e) => setOrderStatus(e.target.value)}
              fullWidth
              sx={{ input: { color: 'black' }, '.MuiSelect-select': { color: 'black' } }}
            >
              {Object.entries(statuses).map(([key, value]) => (
                <MenuItem key={key} value={key} sx={{ color: 'black' }}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Box mt={3}>
          <Button onClick={handleFetchItems} variant="contained" color="primary" sx={{ marginRight: 2 }}>
            <Icon icon="ic:baseline-search" style={{ marginRight: '8px' }} /> Fetch Items
          </Button>
          <Button onClick={downloadCSV} variant="contained" color="primary" sx={{ marginRight: 2 }}>
            <Icon icon="mdi:download" style={{ marginRight: '8px' }} /> Download CSV
          </Button>
        </Box>
      </Box>

      {dataLoading ? (
        <CustomLoader />
      ) : (
        <>
          <Box mb={3}>
            <Typography variant="h6">Store Name: {storeName || 'N/A'}</Typography>
            <Typography variant="h6">Total Items: {totalCount}</Typography>
          </Box>
          <DataTable
            columns={columns}
            data={tableData}
            pagination
            paginationServer
            paginationTotalRows={totalCount}
            paginationDefaultPage={Math.ceil(pagination.skip / pagination.limit) + 1} // Calculate current page
            onChangePage={(page) => {
              const limit = pagination.limit || 10;
              const newSkip = Math.max(0, (page - 1) * limit); // Ensure valid skip value
              setPagination((prev) => ({ ...prev, skip: newSkip }));
              fetchOrderedItems({
                variables: {
                  timeSlotId: selectedTimeSlot,
                  orderDate,
                  orderStatus: parseInt(orderStatus, 10),
                  skip: newSkip,
                  limit,
                },
              });
            }}

            onChangeRowsPerPage={(newLimit) => {
              setPagination((prev) => ({ ...prev, limit: newLimit, skip: 0 }));

              fetchOrderedItems({
                variables: {
                  timeSlotId: selectedTimeSlot,
                  orderDate,
                  orderStatus: parseInt(orderStatus, 10),
                  skip: 0,
                  limit: newLimit,
                },
              });
            }}
            customStyles={customStyles}
          />

        </>
      )}
    </Container>
  );
};

export default ItemsbyTimeSlot;
